import type { Participant, PeerStatus, Presence, Room } from "@catmail/shared";
import { FileText, MessageSquare, Moon, Paperclip, Pencil } from "lucide-react";

import { Avatar, Dialog, ListRow } from "@/components";
import { cn } from "@/lib/cn";
import { FOCUS, PRESS_FLAT } from "@/lib/interaction";

import { avatarToneOf, peersOf, roomTitleOf } from "../../lib/roomView";
import { ActionTile } from "../ActionTile";

/**
 * 메일에는 실시간 접속이라는 게 없다. 시안의 "지금 온라인" 자리에는
 * 대신 "이 사람과 대화가 굴러가고 있는지"를 넣는다.
 */
/**
 * 점 옆에 붙는 상태. **디스코드 문구를 그대로 쓴다.**
 *
 * 점 네 개(초록·노랑·회색·빨강)가 이미 디스코드 그대로다. 거기에 이 앱에서만
 * 쓰는 말을 새로 지어 붙이면 색은 아는데 글자는 처음 보는 것이 되어, 읽는 사람이
 * 뜻을 매번 다시 맞춰봐야 한다. 아는 낱말이 설명보다 빠르다.
 */
const STATUS_TEXT: Record<PeerStatus, string> = {
  active: "온라인",
  awaiting: "자리 비움",
  idle: "오프라인",
  noreply: "방해 금지",
};

const STATUS_DOT: Record<PeerStatus, string> = {
  active: "bg-status-active",
  awaiting: "bg-status-awaiting",
  idle: "bg-status-idle",
  noreply: "bg-status-noreply",
};

/**
 * 같은 서버 사람에게는 **추측 말고 진짜**를 쓴다.
 *
 * 낱말은 위와 같다. 바꾸는 것은 근거뿐이라 새 낱말을 지을 자리가 아니다 —
 * 같은 초록 점을 보고 어떤 사람에게는 "요즘 주고받는 중", 어떤 사람에게는
 * "지금 붙어 있음"이라고 읽어야 한다면 그건 읽는 쪽 일이 아니다.
 */
const PRESENCE_TEXT: Record<Presence, string> = {
  online: "온라인",
  away: "자리 비움",
  offline: "오프라인",
};

const PRESENCE_DOT: Record<Presence, string> = {
  online: "bg-status-active",
  away: "bg-status-awaiting",
  offline: "bg-status-idle",
};

type PeerProfileDialogProps = {
  room: Room | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 이 사람과 같이 있는 다른 방들 */
  sharedRooms: Room[];
  attachmentCount: number;
  /**
   * 누구의 프로필인가. 안 주면 방의 첫 상대를 쓴다.
   * 단체방에서 아바타를 눌렀을 때 **누른 사람**이 떠야 하므로 필요하다.
   */
  peer?: Participant | null;
  onOpenRoom: (roomId: string) => void;
  onMoveToQuiet: (roomId: string) => void;
  onOpenFiles?: () => void;
  onRename: (roomId: string) => void;
  /** 이미 차단한 상대인가. 버튼 문구가 뒤집힌다. */
  blocked?: boolean;
  onBlock: (roomId: string) => void;
};

/** 시안 02-1 · 프로필 누르면 */
export function PeerProfileDialog({
  room,
  open,
  onOpenChange,
  sharedRooms,
  attachmentCount,
  peer: peerOverride,
  onOpenRoom,
  onMoveToQuiet,
  onOpenFiles,
  onRename,
  blocked = false,
  onBlock,
}: PeerProfileDialogProps) {
  const peer = peerOverride ?? (room ? (peersOf(room)[0] ?? null) : null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {room && peer ? (
        <Dialog.Content size="sm">
          <div className="bg-surface-2 border-line-soft relative flex flex-col items-center gap-3.5 border-b px-6.5 pt-6.5 pb-5.5">
            <Dialog.Close />

            <Avatar
              size="3xl"
              initials={peer.initials}
              colorIndex={peer.colorIndex}
              imageUrl={peer.avatarUrl}
              dotTone={avatarToneOf(peer)}
              label={peer.displayName}
            />

            {/*
              긴 이름과 주소가 창을 밀어내지 못하게 한다. 가운데 정렬은 넘치는
              내용을 **양옆으로** 흘리기 때문에, 폭을 묶어두지 않으면 이름이
              길 때 앞글자가 잘려 나간다("SEONG CHAN SEOL" → "CHAN SEOL").
              주소는 붙여 쓴 한 덩어리라 낱말 단위로는 안 끊어져 break-all이 필요하다.
            */}
            <div className="flex w-full min-w-0 flex-col items-center gap-1">
              <Dialog.Title className="max-w-full truncate">
                {peer.displayName}
              </Dialog.Title>
              <Dialog.Description className="max-w-full break-all">
                {peer.address}
              </Dialog.Description>

              <div className="mt-1.25 flex items-center gap-1.5">
                <span
                  className={`size-2 rounded-full ${
                    peer.presence
                      ? PRESENCE_DOT[peer.presence]
                      : STATUS_DOT[peer.status]
                  }`}
                />
                <span className="text-body text-[13px]">
                  {peer.presence
                    ? PRESENCE_TEXT[peer.presence]
                    : STATUS_TEXT[peer.status]}
                </span>
              </div>

              {/*
                같은 서버 사람이라는 것을 알린다. 여기 뜬 상태가 진짜 접속인지
                대화 흐름을 보고 지은 말인지 갈리는 자리이고, 무엇보다 이쪽으로
                보낸 말은 메일로 나가지 않고 바로 간다 — 밖으로 새지 않는다는
                뜻이라 알 만한 값어치가 있다.
              */}
              {peer.internal ? (
                <span className="bg-chip text-muted-2 mt-1.5 rounded-full px-2 py-0.5 text-[11px]">
                  Cat Mail 사용자
                </span>
              ) : null}
            </div>
          </div>

          <div className="flex gap-2 px-5 pt-5 pb-4.5">
            <ActionTile
              icon={<MessageSquare />}
              label="채팅하기"
              onClick={() => onOpenRoom(room.id)}
            />
            <ActionTile
              icon={<Paperclip />}
              label="주고받은 파일"
              onClick={onOpenFiles}
            />
            <ActionTile
              icon={<Moon />}
              label={room.isQuiet ? "대화 목록으로" : "조용한 방으로"}
              onClick={() => onMoveToQuiet(room.id)}
            />
          </div>

          <div className="border-line-soft overflow-y-auto border-t py-1.5">
            {sharedRooms.map((shared) => (
              <ListRow
                key={shared.id}
                chevron
                count={shared.participants.length}
                onClick={() => onOpenRoom(shared.id)}
                leading={
                  <Avatar
                    size="sm"
                    initials={roomTitleOf(shared).slice(0, 2)}
                    colorIndex={shared.participants[1]?.colorIndex ?? 0}
                    label={roomTitleOf(shared)}
                  />
                }
              >
                {roomTitleOf(shared)}
              </ListRow>
            ))}

            <ListRow
              chevron
              count={attachmentCount}
              leading={<FileText />}
              onClick={onOpenFiles}
            >
              받은 파일
            </ListRow>

            <ListRow
              chevron
              leading={<Pencil />}
              onClick={() => onRename(room.id)}
            >
              이름 바꾸기
            </ListRow>
          </div>

          <div className="border-line-soft border-t px-6 py-3.5 pb-4.5">
            {/*
              모달에서 제일 무거운 동작인데 눌린 티도, 초점 테두리도 없었다.
              값은 lib/interaction.ts 한 곳에서 가져온다.
            */}
            <button
              type="button"
              onClick={() => onBlock(room.id)}
              className={cn(
                `-mx-2 rounded-md px-2 py-1 text-sm ${PRESS_FLAT} ${FOCUS}`,
                blocked
                  ? "text-muted-2 hover:bg-surface-3 hover:text-body active:bg-line-soft"
                  : "text-danger hover:bg-danger/8 active:bg-danger/14",
              )}
            >
              {blocked ? "차단 풀기" : "이 사람 차단하기"}
            </button>
            {blocked ? (
              <p className="text-muted-3 mt-1 text-[12px]">
                앞으로 오는 메일을 저장하지 않아요. 이미 받은 건 그대로 있어요.
              </p>
            ) : null}
          </div>
        </Dialog.Content>
      ) : null}
    </Dialog>
  );
}
